import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Center,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { getPartnerInitiatedKycs } from 'api/kyc';
import Pagination from 'modules/approve-transactions-v2/components/Pagination';
import KycPartnerSubmissionDrawer from 'modules/kyc/components/KycPartnerSubmissionDrawer';
import {
  DEFAULT_KYC_PARTNER_CODE,
  DEFAULT_PAGE_SIZE,
  KYC_TAB_TITLES,
  PARTNER_INITIATED_TABLE_HEADERS,
} from 'modules/kyc/constants/constants';
import {
  formatPartnerAccountStatus,
  formatRfiStatus,
  getPartnerAccountStatusBadgeColor,
  getRfiStatusBadgeColor,
  isPartnerSubmissionPending,
} from 'modules/kyc/utils/utils';
import { formatDateTime } from 'utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const truncateKycId = (value) => (value?.length > 15 ? `${value.slice(0, 12)}...` : value);

const truncateSubmitError = (value) => (value?.length > 200 ? `${value.slice(0, 200)}...` : value);

const PartnerInitiatedKycs = () => {
  const [kycs, setKycs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLastPage, setIsLastPage] = useState(true);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [selectedKycId, setSelectedKycId] = useState(null);

  const latestRequestRef = useRef(0);

  const fetchKycs = useCallback(async () => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getPartnerInitiatedKycs({ page, pageSize: DEFAULT_PAGE_SIZE });

      if (requestId !== latestRequestRef.current) return;

      const data = response?.data?.data;

      setKycs(data?.kycs || []);
      setTotalCount(data?.total_count || 0);
      setIsLastPage(data?.is_last_page ?? true);
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;

      setError(err);
      setKycs([]);
    } finally {
      if (requestId === latestRequestRef.current) {
        setIsLoading(false);
      }
    }
  }, [page]);

  useEffect(() => {
    fetchKycs();
  }, [page, fetchKycs]);

  const visibleKycs = useMemo(() => {
    if (showSubmitted) return kycs ?? [];

    return kycs?.filter((kyc) => isPartnerSubmissionPending(kyc?.partner_status)) ?? [];
  }, [kycs, showSubmitted]);

  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

  return (
    <Box overflowX='auto' maxWidth='100%' p={4} borderRadius='lg' boxShadow='sm' bg='white'>
      <Flex align='center' justify='space-between' mt={4} mb={3}>
        <Heading as='h1' size='md' color='gray.700'>
          {KYC_TAB_TITLES.INITIATED_ON_PARTNER}{' '}
          <Badge colorScheme='blue' variant='solid' px={2} ml={1}>
            {totalCount}
          </Badge>
        </Heading>
        <Checkbox isChecked={showSubmitted} onChange={(e) => setShowSubmitted(e.target.checked)}>
          Show already submitted
        </Checkbox>
      </Flex>

      <LoadingErrorWrapper isLoading={isLoading} errorTitle='Error fetching partner initiated KYCs.' error={error}>
        <>
          <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto' mt={4}>
            <Table variant='simple' size='md'>
              <Thead bg='gray.100' position='sticky' top={0} zIndex={1}>
                <Tr>
                  {PARTNER_INITIATED_TABLE_HEADERS.map((heading, idx) => (
                    <Th key={idx} fontSize='sm' fontWeight='bold' color='gray.700' py={3}>
                      {heading}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {visibleKycs?.map((row) => (
                  <Tr
                    key={row?.kyc_id}
                    fontSize='sm'
                    cursor='pointer'
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => setSelectedKycId(row?.kyc_id)}
                  >
                    <Td>{row?.created_at ? formatDateTime(row?.created_at) : '-'}</Td>
                    <Td color='gray.600' fontWeight='bold'>
                      <Tooltip label={row?.kyc_id} bg='gray.600'>
                        <Text fontSize='sm' isTruncated>
                          {truncateKycId(row?.kyc_id)}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td color='gray.600' fontWeight='bold'>
                      {row?.entity_name || '-'}
                    </Td>
                    <Td color='gray.600'>
                      <Text fontSize='sm'>{row?.risk_level || '-'}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getRfiStatusBadgeColor(row?.kyc_status)} rounded='full' px={3}>
                        {formatRfiStatus(row?.kyc_status)}
                      </Badge>
                    </Td>
                    <Td>
                      <Tooltip label={truncateSubmitError(row?.last_submit_error) || ''} bg='gray.600'>
                        <Badge
                          colorScheme={getPartnerAccountStatusBadgeColor(row?.partner_status)}
                          rounded='full'
                          px={3}
                        >
                          {formatPartnerAccountStatus(row?.partner_status)}
                        </Badge>
                      </Tooltip>
                    </Td>
                    <Td color='gray.600'>{row?.partner_account_id || '-'}</Td>
                    <Td color='gray.600'>{row?.pending_documents?.join(', ') || '-'}</Td>
                    <Td>
                      <IconButton
                        aria-label='Open partner submission'
                        icon={<ArrowForwardIcon />}
                        isRound
                        size='sm'
                        onClick={() => setSelectedKycId(row?.kyc_id)}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {!visibleKycs?.length && (
            <Center>
              <Text color='gray.500' mt={4}>
                No partner initiated KYCs found.
              </Text>
            </Center>
          )}

          {totalPages > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPreviousPage={() => setPage((prev) => Math.max(prev - 1, 1))}
              onNextPage={() => setPage((prev) => prev + 1)}
              isNextDisabled={isLastPage}
            />
          )}
        </>
      </LoadingErrorWrapper>

      <KycPartnerSubmissionDrawer
        isOpen={Boolean(selectedKycId)}
        onClose={() => setSelectedKycId(null)}
        kycId={selectedKycId}
        partnerCode={DEFAULT_KYC_PARTNER_CODE}
        onSubmitted={fetchKycs}
      />
    </Box>
  );
};

export default PartnerInitiatedKycs;
